import { reactive, ref, onMounted, onUnmounted } from 'vue';
import { databases, appwriteConfig, query, client, functions } from './appwrite';

const DATABASE_ID = appwriteConfig.database;
const COLLECTION_ID = appwriteConfig.collections.legs;

let instance = null;
let unsubscribe = null;

export function useLegs() {
    if (instance) return instance;

    const legs = reactive([]);
    const isInitialized = ref(false);
    const locallyCompletingLegIds = new Set();

    const parseLeg = (doc) => {
        // Adjust the parsing logic based on your Legs collection schema
        return doc;
    };

    const fetchLegs = async () => {
        if (!isInitialized.value) {
            initialize();
        }
        try {
            const response = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, [
                query.limit(5000)
            ]);
            legs.splice(0, legs.length, ...response.documents.map(parseLeg));
        } catch (err) {
            console.error('Error fetching legs:', err);
        }
    };

    const initialize = () => {
        if (isInitialized.value) return;

        let reconnectTimeout = null;

        // Create subscription function that can be reused
        const createSubscription = () => {
            // Clean up any existing subscription before creating a new one
            if (unsubscribe) {
                unsubscribe();
            }

            return client.subscribe(`databases.${DATABASE_ID}.collections.${COLLECTION_ID}.documents`,
                (response) => {
                    response.events.forEach(event => {
                        const doc = response.payload;
                        if (event.includes('create')) {
                            if (!legs.find(l => l.$id === doc.$id)) {
                                legs.push(parseLeg(doc));
                            }
                        } else if (event.includes('update')) {
                            const existingLeg = legs.find(l => l.$id === doc.$id);
                            if (existingLeg) {
                                Object.assign(existingLeg, parseLeg(doc));
                            }
                        } else if (event.includes('delete')) {
                            const index = legs.findIndex(l => l.$id === doc.$id);
                            if (index !== -1) {
                                if (locallyCompletingLegIds.has(doc.$id)) {
                                    legs[index].deletedLocally = true;
                                } else {
                                    legs.splice(index, 1);
                                }
                            }
                        }
                    });
                },
                // Add error callback
                (error) => {
                    console.error('Subscription error:', error);
                    // Clear any existing timeout
                    if (reconnectTimeout) {
                        clearTimeout(reconnectTimeout);
                    }
                    // Attempt to reconnect after 5 seconds
                    reconnectTimeout = setTimeout(() => {
                        console.log('Attempting to reconnect...');
                        unsubscribe = createSubscription();
                    }, 5000);
                }
            );
        };

        // Initial subscription
        unsubscribe = createSubscription();

        // Cleanup function that removes the subscription and any pending reconnection
        const cleanup = () => {
            if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
            }
            if (unsubscribe) {
                unsubscribe();
            }
        };

        onMounted(fetchLegs);
        onUnmounted(cleanup);

        isInitialized.value = true;
    };

    const completeLeg = async (legId) => {
        locallyCompletingLegIds.add(legId);
        try {
            const response = await functions.createExecution(appwriteConfig.functions.completeLeg, JSON.stringify({ legId }));
            const result = JSON.parse(response.responseBody);

            if (result.success) {
                const existingLeg = legs.find(l => l.$id === legId);
                if (existingLeg) existingLeg.deletedLocally = true;
            } else {
                locallyCompletingLegIds.delete(legId);
            }

            return result;
        } catch (err) {
            console.error('Error completing leg:', err);
            locallyCompletingLegIds.delete(legId);
            return { success: false, message: 'Failed to mark leg as complete.', error: err.toString() };
        }
    };

    instance = {
        legs,
        fetchLegs,
        initialize,
        completeLeg
    };

    return instance;
}